import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiCall } from '../utils/api';

function TribeRunbookSelection() {
  const navigate = useNavigate();
  const location = useLocation();

  const { tribe, incident_number, incident_description } = location.state || {};

  const [runbooks, setRunbooks] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [runbookRequested, setRunbookRequested] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const rowsPerPage = 10;

  useEffect(() => {
    if (!tribe || !incident_number) {
      navigate('/incident_view');
      return;
    }
    fetchRunbooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    setFiltered(
      runbooks.filter(
        (rb) =>
          !term ||
          (rb['Runbook Title'] || '').toLowerCase().includes(term) ||
          (rb['Runbook ID'] || '').toLowerCase().includes(term) ||
          (rb.Solution || '').toLowerCase().includes(term)
      )
    );
    setCurrentPage(1);
  }, [searchTerm, runbooks]);

  const fetchRunbooks = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/runbooks');
      const tribeRunbooks = (data || []).filter((rb) => rb.Tribe === tribe);
      setRunbooks(tribeRunbooks);
      setFiltered(tribeRunbooks);
    } catch (err) {
      console.error('Error fetching runbooks:', err);
      setError(err.message || 'Failed to load runbooks');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRunbook = (runbookId) => {
    navigate(`/resolution?runbook_id=${runbookId}&incident_number=${incident_number}`);
  };

  const handleRequestRunbook = async () => {
    setIsRequesting(true);
    try {
      const data = await apiCall('/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          incident_number,
          request_runbook: true,
          tribe,
          incident_description,
        }),
      });
      if (data.success) {
        setRunbookRequested(true);
        alert('Runbook request submitted. The team will be notified to create one.');
      } else {
        alert(data.error || 'Error submitting runbook request.');
      }
    } catch (err) {
      console.error('Request Runbook Error:', err);
      alert('An error occurred while submitting the runbook request.');
    } finally {
      setIsRequesting(false);
    }
  };

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const currentRows = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  if (loading) {
    return (
      <div className="container-fluid mt-4 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">Loading runbooks for {tribe}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>Select Runbook</h2>
          <p className="text-muted mb-0">
            Incident: <strong>{incident_number}</strong> &mdash; Tribe: <strong>{tribe}</strong>
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {/* Request New Runbook */}
      <div className="d-flex justify-content-end mb-3">
        <button
          className={`btn ${runbookRequested ? 'btn-secondary' : 'btn-outline-warning'}`}
          disabled={runbookRequested || isRequesting}
          onClick={handleRequestRunbook}
        >
          {isRequesting
            ? 'Submitting...'
            : runbookRequested
            ? '✔ Runbook Requested'
            : '📋 Request New Runbook'}
        </button>
      </div>
      {runbookRequested && (
        <div className="alert alert-success py-2 mb-3">
          Your request has been recorded. The team will create a runbook for this incident.
        </div>
      )}

      {/* Search */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by title, ID or solution..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '20%' }}>Runbook ID</th>
              <th style={{ width: '50%', textAlign: 'left' }}>Runbook Title</th>
              <th style={{ width: '20%', textAlign: 'left' }}>Solution</th>
              <th style={{ width: '10%', textAlign: 'center' }}>Select</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((runbook) => (
                <tr key={runbook['Runbook ID']}>
                  <td style={{ verticalAlign: 'middle' }}>{runbook['Runbook ID']}</td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                    {runbook['Runbook Title'] || 'Untitled'}
                  </td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                    {runbook.Solution || 'N/A'}
                  </td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSelectRunbook(runbook['Runbook ID'])}
                    >
                      Proceed
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4 text-muted">
                  {searchTerm ? 'No runbooks match your search.' : `No runbooks found for the ${tribe} tribe.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="mx-3">
            Page {currentPage} of {totalPages} ({filtered.length} runbooks)
          </span>
          <button
            className="btn btn-outline-secondary ms-2"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default TribeRunbookSelection;
